import Head from "next/head";
import Layout from "../components/Layout";
import Breadcrumb from "@/components/BreadCrumb";

export default function Privacy() {
  return (
    <Layout>
      <Head>
        <title>Privacy Policy | AstroLearn</title>
        <meta
          name="description"
          content="Read AstroLearn's privacy policy to understand how we protect your data while offering secure access to our online astrology courses."
        />
      </Head>
      <section>
        <Breadcrumb classNames="absolute top-20 left-4 lg:left-20" />
        <div className="container prose my-10 max-md:mt-12">
          <h1>Privacy Policy</h1>

          <h2>Privacy Statement</h2>
          <p>
            ESH VALUE ASTROJI SERVICES PVT LTD (referred to as &quot;the
            Company&quot; or &quot;AstroLearn&quot;) values the privacy of users
            who access the website{" "}
            <a href="https://www.astrolearn.co/">https://www.astrolearn.co/</a>{" "}
            or engage with the Company through the web.
          </p>

          <h2>Commitment to Confidentiality</h2>
          <p>
            AstroLearn is dedicated to upholding the confidentiality of all data
            and information within its possession and is fully committed to
            safeguarding the privacy and security of user data. The Future
            University is a web-based platform utilized by educators, trainers,
            coaches, and others to establish an online presence, cultivate a
            personal brand, launch cohort-based courses, and create a community
            of learners.
          </p>

          <h2>User Consent and Acknowledgment</h2>
          <p>
            All users of AstroLearn acknowledge and consent to their use of the
            web platform via computers or smartphones. The users of services
            provided by educators, trainers, coaches, etc., on the web encompass
            students, learners, professionals, and others. Given the diverse
            user base of AstroLearn, we are conscientious of the data we
            collect, utilize, process, store, share, and disclose.
          </p>

          <h2>Integration with Terms and Conditions</h2>
          <p>
            This policy should always be read and interpreted in conjunction
            with the website&apos;s terms and conditions.
          </p>

          <h2>User Authentication</h2>
          <p>
            The Company does not distinguish between individuals accessing the
            website from a device, as long as the login/access credentials
            match. To optimize website usage and ensure accurate information
            capture, it is imperative to log in using one&apos;s own
            credentials.
          </p>

          <h2>Terminology</h2>
          <p>
            In this policy, the pronouns &quot;We,&quot; &quot;Us,&quot; and
            &quot;Our&quot; collectively refer to the Company, and the pronouns
            &quot;You,&quot; &quot;Your,&quot; and &quot;Yourself&quot; are
            synonymous with &quot;user&quot;. Terms not explicitly defined here
            shall carry the meanings attributed to them in the Terms of Use.
          </p>

          <h2>Privacy Policy Scope</h2>
          <p>
            This privacy policy, along with any referenced documents, outlines
            AstroLearn&apos;s stance and practices regarding the collection,
            processing, utilization, disclosure, storage, and transfer of Your
            information (the &quot;Privacy Policy&quot;). By engaging with Our
            web platform, including accessing it, using the services, and
            clicking the &quot;I accept&quot; button provided on the web, You
            express acceptance of Our information practices and this Privacy
            Policy. If the information provider, including personal information,
            is based in India, this Privacy Policy is considered an electronic
            record under Indian data privacy laws, and it does not necessitate a
            physical, electronic, or digital signature from The Future
            University.
          </p>

          <h2>Information Collection</h2>

          <h3>Information We May Collect from You</h3>
          <p>
            We collect and store information, including personal details, about
            You and Your interactions with Our web platform and services. This
            information is gathered through various means, as detailed below.
            Throughout this Privacy Policy, personal information refers to data
            capable of identifying a natural person directly or indirectly. Any
            other information (non-personal information) is data that does not
            lead to direct identification. Non-personal information may be
            collected, processed, used, disclosed, stored, and transferred for
            any purpose. While You have the option to withhold personal
            information, many such details are necessary to access Our web and
            services, and failure to provide them may restrict Your access.
          </p>

          <ul>
            <li>
              Information provided on or through Our web or services, such as
              during registration, payment, or content posting.
            </li>
            <li>
              Details of Your web usage, including traffic and location data,
              weblogs, and communication patterns.
            </li>
            <li>
              Information related to Your activities and interactions on the
              web, including correspondence, internet protocol addresses, device
              and software data, and records of communication with Us.
            </li>
            <li>
              Data from surveys that You may choose to complete voluntarily.
            </li>
            <li>
              Content uploaded by You or other registered users in relation to
              You.
            </li>
          </ul>

          <p>
            We also gather impersonal information necessary to provide and
            optimize Our services and Your interactions.
          </p>

          <h3>User Representations and Warranties</h3>
          <p>Users warrant and represent to the Company that:</p>
          <ul>
            <li>
              All information provided is accurate, up-to-date, and correct.
            </li>
            <li>
              The provision of information does not breach any third-party
              agreements, laws, decrees, orders, or judgments.
            </li>
            <li>
              If information belongs to a third party, the user is authorized by
              the third party to use, access, and distribute it.
            </li>
            <li>
              The Company&apos;s officers, directors, contractors, or agents are
              not accountable for the authenticity or truthfulness of
              user-provided information.
            </li>
            <li>
              Users will indemnify and hold harmless the Company, its officers,
              directors, contractors, agents, and third parties relying on
              user-provided information in case of a breach of this Privacy
              Policy.
            </li>
          </ul>

          <h3>Internet Protocol Addresses</h3>
          <p>
            We may collect device information, including internet protocol
            addresses, operating systems, and browser types for system
            management and to provide aggregate data to advertisers and service
            providers. This information is statistical and does not identify
            individuals.
          </p>

          <h3>Cookies</h3>
          <p>
            We and Our service providers employ cookies and similar technologies
            (like web beacons) for various purposes. These technologies make it
            easier to access Our web and services, remember user preferences,
            improve user experiences, and analyze data. Cookies are small data
            files stored on devices during web usage. They help recognize repeat
            visits, store settings, and enhance web efficiency. Cookies do not
            identify individuals personally and do not enable identification on
            third-party websites.
          </p>

          <h3>Disclaimer</h3>
          <p>
            Some web features may be affected if cookies are disabled. Links on
            the web may lead to third-party sites with different privacy
            policies. Users access these links at their discretion, and the
            Company is not liable for third-party privacy breaches or
            objectionable content.
          </p>

          <h2>Data Storage</h2>

          <h3>Where Your Personal Information Is Stored</h3>
          <p>
            Data collected from You may be processed, stored, and maintained in
            systems located in India. If You are outside India and provide
            information, it may be transferred and processed in India.
            Additionally, Your information may be handled by staff outside India
            working for Us or service providers. By submitting information, You
            consent to transfer, storage, and processing as described.
          </p>

          <h2>Security Measures</h2>
          <p>
            We implement reasonable administrative, technical, physical, and
            managerial measures to protect personal information from
            unauthorized access. Industry-standard encryption protocols secure
            personal data transmitted to Us during registration and sign-up.
          </p>

          <p>
            While We prioritize data security, we cannot guarantee absolute
            security. Usage and transmission of data are at Your risk. Users
            must ensure appropriate safeguards to maintain data integrity.
          </p>

          <h2>User Access and Rights</h2>

          <h3>User Rights</h3>
          <p>
            Users can request not to process personal information for marketing.
            You can decline this processing during information collection or via
            email communication. Withdrawal of consent will affect access to Our
            web and services. In such cases, Your information may be deleted or
            de-identified.
          </p>

          <h2>Intellectual Property and Changes</h2>

          <h3>Intellectual Property</h3>
          <p>
            AstroLearn is the Company&apos;s intellectual property. Materials on
            the website are protected by copyright, trademark, and intellectual
            property laws. Unauthorized use may breach these laws.
          </p>

          <h3>Changes to Our Privacy Policy</h3>
          <p>
            We may update this Privacy Policy periodically. Amendments will be
            effective upon posting. While We may notify users of changes,
            it&apos;s Your responsibility to stay informed. Continued use
            implies acceptance of changes.
          </p>

          <h3>Confidentiality and Liability</h3>

          <h3>Confidentiality</h3>
          <p>
            Users commit to treating accessed data with utmost confidentiality
            and not disclosing, sharing, manipulating, selling, or using data in
            violation of this Privacy Policy. Users will use information for The
            Future University&apos;s benefit within business needs. Breach of
            confidentiality is subject to legal remedies.
          </p>

          <h3>Limitation of Liability</h3>
          <p>
            The Company is not liable for third-party actions or their breaches.
            Users indemnify the Company and its officers in case of a breach.
            Information transmitted may have errors or unauthorized access. The
            Company is not liable for third-party breaches or content. Users
            confirm accurate data submission and are liable for damages from
            false data. The Company implements reasonable security but is not
            liable for unauthorized disclosures by third parties.
          </p>

          <h2>Severability and Google API Services</h2>

          <h3>Severability</h3>
          <p>
            Provisions in this Privacy Policy are enforceable individually.
            Unenforceable clauses won&apos;t affect others.
          </p>

          <h3>Disclosure for Google API Services</h3>
          <p>
            AstroLearn&apos;s utilization of information obtained through Google
            APIs is aligned with{" "}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noreferrer"
            >
              Google API Services User Data Policy
            </a>
            , which includes adherence to the{" "}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes"
              target="_blank"
              rel="noreferrer"
            >
              Limited Use requirements
            </a>
            .
          </p>

          <h3>Limited Utilization</h3>
          <p>
            Our application strictly adheres to all stipulations outlined in
            Google&apos;s limited use policy.
          </p>
          <ul>
            <li>
              Human access to user data is restricted unless explicit user
              consent has been secured for viewing specific messages, files, or
              other data.
            </li>
            <li>
              The data is not utilized or shared for ad-serving purposes,
              encompassing retargeting, personalized, or interest-based
              advertising.
            </li>
            <li>
              Data usage is confined to enhancing or providing user-facing
              features that prominently appear within the requesting
              application&apos;s user interface. All other uses of the data are
              prohibited.
            </li>
            <li>
              Data is only transferred to other entities when essential for
              enhancing or providing user-facing features prominently displayed
              within the requesting application&apos;s user interface.
            </li>
          </ul>

          <h2>Contact Us</h2>
          <p>
            For inquiries, you can write an email to{" "}
            <a href="mailto:ceo@thefuture.university">
              ceo@thefuture.university
            </a>
            .
          </p>
        </div>
      </section>
    </Layout>
  );
}
